"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  TypAvatara,
  AvatarData,
  getSignedAvatarUrl,
  PLACEHOLDER_AVATAR, //todo
  maRealnyAvatar,
} from "@/lib/avatar-utils";
import { useEffect, useState } from "react";

interface AvatarOsobyProps {
  typ: TypAvatara;
  id: string;
  meno: string;
  avatarData?: AvatarData;
  pozicia?: string;
  className?: string;
  fallbackClassName?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

export function AvatarOsoby({
  typ,
  id,
  meno,
  avatarData,
  pozicia,
  className,
  fallbackClassName,
  size = "md",
}: AvatarOsobyProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getSizeClass = () => {
    switch (size) {
      case "sm":
        return "h-8 w-8";
      case "lg":
        return "h-12 w-12";
      case "xl":
        return "h-16 w-16";
      default:
        return "h-10 w-10";
    }
  };

  const getPoziciaColor = (pozicia?: string) => {
    switch (pozicia?.toLowerCase()) {
      case "brankar":
        return "from-blue-500 to-blue-600";
      case "obranca":
        return "from-green-500 to-green-600";
      case "utocnik":
        return "from-red-500 to-red-600";
      default:
        return "from-muted to-muted-foreground";
    }
  };

  const getInicialky = () => {
    return meno
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    const nacitajAvatar = async () => {
      setIsLoading(true);

      // Ak nemá reálny avatar, použi placeholder
      if (!maRealnyAvatar(avatarData)) {
        setAvatarUrl(PLACEHOLDER_AVATAR);
        setIsLoading(false);
        return;
      }

      // Pre hráčov použi verejnú URL
      if (typ === "hrac") {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
        setAvatarUrl(
          `${supabaseUrl}/storage/v1/object/public/avatary/players/${id}`,
        );
        setIsLoading(false);
        return;
      }

      // Pre profily a návrhy použi signed URL
      const signedUrl = await getSignedAvatarUrl(typ, id);
      setAvatarUrl(signedUrl);
      setIsLoading(false);
    };

    nacitajAvatar();
  }, [typ, id, avatarData]);

  const src = isLoading ? PLACEHOLDER_AVATAR : avatarUrl || PLACEHOLDER_AVATAR;

  return (
    <Avatar
      className={cn(getSizeClass(), className)}
      data-avatar-typ={typ}
      data-avatar-id={id}
    >
      <AvatarImage
        src={src}
        alt={meno}
        onError={(e) => {
          // Fallback na placeholder ak sa obrázok nepodarí načítať
          (e.target as HTMLImageElement).src = PLACEHOLDER_AVATAR;
        }}
      />
      <AvatarFallback
        className={cn(
          "bg-linear-to-br text-white font-bold",
          getPoziciaColor(pozicia),
          fallbackClassName,
        )}
      >
        {getInicialky()}
      </AvatarFallback>
    </Avatar>
  );
}
